import { ILigaEvent } from '@/model/liga-event.model';
import { ILigaEventRegistration } from '@/model/liga-event-registration.model';

export interface IAdminPaymentItem {
  id: number;
  /** event — регистрация; release — покупка релиза */
  paymentKind?: 'event' | 'release';
  ligaEventId?: number | null;
  releaseId?: number | null;
  ligaEventTitle: string;
  userId: number;
  userLogin?: string;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  amountPaidKopecks: number;
  paidAt: string;
  refundedAt?: string | null;
  yooMoneyPaymentId?: string | null;
}
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const getToken = async (): Promise<string> => {
  const { value } = await Preferences.get({ key: 'jwt' });
  return value || '';
};

const baseUrl = () => process.env.VUE_APP_API_URL || '';

const api = async <T>(method: string, path: string, body?: unknown, params?: Record<string, string>): Promise<T> => {
  const token = await getToken();
  const url = `${baseUrl()}api${path}`;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (isPlatform('ios') || isPlatform('android')) {
    const opts: any = { url, method, headers, readTimeout: 60000 };
    if (body != null) {
      headers['Content-Type'] = 'application/json';
      opts.data = JSON.stringify(body);
    }
    if (params) opts.params = params;
    const res = await CapacitorHttp.request(opts);
    if (res.status >= 400) throw new Error((res.data as any)?.message || `HTTP ${res.status}`);
    return res.data as T;
  }
  const config: any = { url: `api${path}`, method, headers };
  if (body) config.data = body;
  if (params) config.params = params;
  const { data } = await axios.request<T>(config);
  return data;
};

export default {
  /** Список событий: предстоящие; past — прошедшие; all — все (админ/менеджер). */
  async getEvents(options?: boolean | { all?: boolean; past?: boolean }): Promise<ILigaEvent[]> {
    let params: Record<string, string> | undefined;
    if (typeof options === 'boolean') {
      params = options ? { all: 'true' } : undefined;
    } else if (options) {
      params = {};
      if (options.all) params.all = 'true';
      if (options.past) params.past = 'true';
      if (Object.keys(params).length === 0) params = undefined;
    }
    return api<ILigaEvent[]>('GET', '/liga-events', undefined, params);
  },

  async getEvent(id: number): Promise<ILigaEvent | null> {
    try {
      return await api<ILigaEvent>('GET', `/liga-events/${id}`);
    } catch {
      return null;
    }
  },

  /** Регистрации по событию (админ). */
  async getRegistrations(eventId: number): Promise<ILigaEventRegistration[]> {
    return api<ILigaEventRegistration[]>('GET', `/liga-events/${eventId}/registrations`);
  },

  /** Мои регистрации. */
  async getMyRegistrations(): Promise<ILigaEventRegistration[]> {
    return api<ILigaEventRegistration[]>('GET', '/liga-events/my-registrations');
  },

  /** Синхронизировать статус оплаты с ЮKassa (вызов со страницы успеха после возврата). */
  async syncMyPayment(eventId: number): Promise<void> {
    try {
      await api('GET', '/liga-events/sync-my-payment', undefined, { eventId: String(eventId) });
    } catch {
      // игнорируем ошибки (не авторизован, нет регистрации и т.д.)
    }
  },

  /** Регистрация на событие. */
  async register(eventId: number, priceTierId?: number): Promise<ILigaEventRegistration> {
    return api<ILigaEventRegistration>('POST', `/liga-events/${eventId}/register`, { priceTierId });
  },

  /** Отменить участие (если событие ещё не началось). */
  async cancelRegistration(registrationId: number): Promise<void> {
    await api('DELETE', `/liga-events/registrations/${registrationId}`);
  },

  /** Создать платёж (возвращает redirectUrl). При нескольких тарифах — priceTierId; для чека — customerEmail. В нативном приложении передаёт returnToApp: true для возврата в приложение после оплаты. */
  async createPayment(
    registrationId: number,
    priceTierId?: number,
    customerEmail?: string
  ): Promise<{ paymentId: string; redirectUrl: string; amountKopecks: number }> {
    const body: { priceTierId?: number; customerEmail?: string; returnToApp?: boolean } = {};
    if (priceTierId != null) body.priceTierId = priceTierId;
    if (customerEmail != null && customerEmail.trim()) body.customerEmail = customerEmail.trim();
    if (Capacitor.isNativePlatform()) body.returnToApp = true;
    return api('POST', `/liga-events/registrations/${registrationId}/create-payment`, Object.keys(body).length ? body : undefined);
  },

  /** Список всех оплат для админки (оплаченные регистрации, включая с возвратом). */
  async getAdminPayments(): Promise<IAdminPaymentItem[]> {
    return api<IAdminPaymentItem[]>('GET', '/admin/payments');
  },

  /** Массовая синхронизация статусов: все регистрации с yoo_money_payment_id и без paid_at проверяются в ЮKassa. */
  async syncPendingPayments(): Promise<{ checkedCount: number; syncedCount: number }> {
    return api<{ checkedCount: number; syncedCount: number }>('POST', '/admin/payments/sync-pending');
  },

  /** Возврат оплаты (админ). Полный возврат — body пустой; частичный — { amountKopecks: number }. */
  async refundRegistration(registrationId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/payments/registrations/${registrationId}/refund`, body);
  },

  /** Отметить оплату как возвращённую вручную (если возврат уже сделан в ЮKassa, а статус не обновился). */
  async markRegistrationRefunded(registrationId: number): Promise<void> {
    await api('POST', `/admin/payments/registrations/${registrationId}/mark-refunded`);
  },

  async refundReleasePurchase(purchaseId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/payments/release-purchases/${purchaseId}/refund`, body);
  },

  async markReleasePurchaseRefunded(purchaseId: number): Promise<void> {
    await api('POST', `/admin/payments/release-purchases/${purchaseId}/mark-refunded`);
  },

  /** Ответ проверки QR: регистрация и флаг «проход уже был выполнен». */
  async verifyQr(
    eventId: number,
    registrationId: number,
    checkIn?: boolean
  ): Promise<{ registration: ILigaEventRegistration; alreadyCheckedIn: boolean } | null> {
    const params: Record<string, string> = { eventId: String(eventId), registrationId: String(registrationId) };
    if (checkIn) params.checkIn = 'true';
    try {
      const res = await api<{ registration: ILigaEventRegistration; alreadyCheckedIn: boolean }>(
        'GET',
        '/liga-events/verify-qr',
        undefined,
        params
      );
      return res;
    } catch {
      return null;
    }
  },

  /** URL логотипа события. width — опционально, для thumbnail (ускоряет загрузку). */
  logoUrl(logoPath: string | undefined, width?: number): string {
    if (!logoPath) return '';
    const base = baseUrl().replace(/\/$/, '');
    let url = `${base}/api/liga-events/logo?path=${encodeURIComponent(logoPath)}`;
    if (width != null && width > 0) {
      url += `&width=${width}`;
    }
    return url;
  },

  // ——— Админ ———
  async createEvent(event: Partial<ILigaEvent>): Promise<ILigaEvent> {
    return api<ILigaEvent>('POST', '/liga-events', event);
  },

  async updateEvent(id: number, event: Partial<ILigaEvent>): Promise<ILigaEvent> {
    return api<ILigaEvent>('PUT', `/liga-events/${id}`, { ...event, id });
  },

  async deleteEvent(id: number): Promise<void> {
    await api('DELETE', `/liga-events/${id}`);
  },

  async setPriceTiers(eventId: number, tiers: { id?: number; name: string; amountKopecks: number }[]): Promise<ILigaEvent['priceTiers']> {
    return api('PUT', `/liga-events/${eventId}/price-tiers`, tiers);
  },

  async uploadLogo(eventId: number, file: File): Promise<void> {
    const token = await getToken();
    const base = baseUrl().replace(/\/$/, '');
    const url = `${base}/api/liga-events/${eventId}/logo`;

    if (isPlatform('ios') || isPlatform('android')) {
      // На iOS/Android используем @capacitor/file-transfer (multipart по пути к файлу)
      const ext = file.name.split('.').pop() || 'png';
      const tempPath = `logo_upload_${Date.now()}.${ext}`;
      try {
        const base64 = await fileToBase64(file);
        await Filesystem.writeFile({
          path: tempPath,
          data: base64,
          directory: Directory.Cache,
        });
        const { uri } = await Filesystem.getUri({
          path: tempPath,
          directory: Directory.Cache,
        });
        const result = await FileTransfer.uploadFile({
          url,
          path: uri,
          fileKey: 'file',
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
          connectTimeout: 60000,
        });
        const code = Number(result.responseCode);
        if (code >= 400) {
          let msg = `HTTP ${code}`;
          if (result.response) {
            try {
              const body = JSON.parse(result.response);
              if (body?.message) msg = body.message;
            } catch {
              // ответ не JSON
            }
          }
          throw new Error(msg);
        }
      } finally {
        try {
          await Filesystem.deleteFile({
            path: tempPath,
            directory: Directory.Cache,
          });
        } catch {
          // игнорируем ошибку удаления временного файла
        }
      }
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    await axios.post(`api/liga-events/${eventId}/logo`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 60000,
    });
  }
};
